import React, { useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/react-hooks';

import S from './group-details-page.module.scss';
import { GroupDancerList } from './group-list';
import { GroupPageAside } from './group-aside/group-aside';
import { GroupHeader } from './group-header/group-header';
import { LoadingDiscoWithContainer } from '../../../../common/loading/loading-disco';
import { useBusiness, useUser } from '../../../../../graphql/graph-hooks';
import { GROUP_PAGE_GQL } from '../../../../../graphql/queries/group-detail-page-query';
import { useTranslation } from 'react-i18next';
import { useFetchMoreDancers } from './useFetchMoreDancers';

const GroupDetailsPage = () => {
  const { t } = useTranslation();
  const { admin: isAdmin } = useUser();
  const { groupId } = useParams();
  const { id: business_id } = useBusiness();
  const [ dancersFilter, setDancersFilter ] = useState('');
  const [sortAscending, setSortAscending] = useState(true);
  const [selectedDancers, setSelectedDancers ] = useState([]);

  const queryVariables = {
    groupId,
    isAdmin,
    hasLocation: false,
    blockedByBusinessId: business_id,
    inGroupBusinessId: business_id,
    first: 20,
    dancersNameLike: dancersFilter,
    orderBy: 'NAME',
    order: sortAscending ? 'ASC' : 'DESC',
  };

  const { loading, error, data, fetchMore } = useQuery(GROUP_PAGE_GQL, {
    fetchPolicy: 'network-only',
    variables: queryVariables,
  });

  const {
    isLoading: isFetchMoreDancersLoading,
    fetchMore: fetchMoreDancers,
    error: fetchMoreError,
  } = useFetchMoreDancers();

  const handleGetMore = () => {
    if(!data?.group.dancers_paginated.pageInfo.hasNextPage || isFetchMoreDancersLoading) {
      return;
    }

    fetchMoreDancers(fetchMore, {
      dancersEndCursor: data?.group.dancers_paginated.pageInfo.endCursor,
    });
  };

  const onDancersRemove = (cache, dancerIds) => {
    const cachedData = cache.readQuery({ query: GROUP_PAGE_GQL, variables: queryVariables });
    if (!cachedData) {
      return;
    }

    const { group, preapproved_shifts } = cachedData;

    cache.writeQuery({
      query: GROUP_PAGE_GQL,
      variables: queryVariables,
      data: {
        group: {
          ...group,
          dancers_paginated: {
            ...group.dancers_paginated,
            nodes: group.dancers_paginated.nodes.filter(({ id }) => dancerIds.indexOf(id) < 0),
          }
        },
        preapproved_shifts: preapproved_shifts
      },
    });
  };

  const selectDancers = (dancerIds) => {
    let dancersToSelect = dancerIds || data.group.dancers_paginated.nodes.map(({id}) => id);

    const uniqueDancers = new Set([...selectedDancers, ...dancersToSelect]);
    setSelectedDancers([...uniqueDancers]);
  };

  const deselectDancers = (dancerIds) => {
    let dancersToDeselect = dancerIds || data.group.dancers_paginated.nodes.map(({id}) => id);

    setSelectedDancers(selectedDancers.filter((id) => dancersToDeselect.indexOf(id) < 0));
  };

  if (loading) return <LoadingDiscoWithContainer />;
  if (error || fetchMoreError) return <h1 className={S.errorPage}>{t('errors.generic')}</h1>;

  return (
    <div className={S.pageWrapper}>
      <GroupHeader
        name={data.group.name}
        groupId={groupId}
        dancers={data.group.dancers_paginated.nodes}
        isDefaultGroup={data.group.default}
        onFilterChange={setDancersFilter}
        filterValue={dancersFilter}
        onDancersRemove={onDancersRemove}
        selectedDancers={selectedDancers}
        selectDancers={selectDancers}
        deselectDancers={deselectDancers}
      />

      <div className={S.groupDetailBottom}>
        <GroupDancerList
          isAdmin={isAdmin}
          groupId={groupId}
          sortAscending={sortAscending}
          onSortChange={() => setSortAscending(!sortAscending)}
          dancerList={data.group.dancers_paginated.nodes}
          onDancersRemove={onDancersRemove}
          selectedDancers={selectedDancers}
          selectDancers={selectDancers}
          deselectDancers={deselectDancers}
          onScrollBottom={handleGetMore}
          isLoading={isFetchMoreDancersLoading}
        />
        <GroupPageAside timezone={data.group.business.timezone} groupId={groupId} />
      </div>
    </div>
  );
};

GroupDetailsPage.displayName = 'GroupDetailsPage';

export { GroupDetailsPage };
